'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Browse', href: '/browse' },
  { label: 'New Releases', href: '/new-releases' },
  { label: 'My List', href: '/my-list' },
];

interface TopbarProps {
  transparent?: boolean;
  currentPath?: string;
}

export default function Topbar({ transparent = false, currentPath = '/' }: TopbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, loading, signOut, isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTransparent = transparent && !scrolled;

  const displayName: string =
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Account';

  const initials = displayName
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    setMobileOpen(false);
    await signOut();
    window.location.href = '/';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent ? 'bg-transparent' : 'topbar-blur'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 h-14 sm:h-16 flex items-center justify-between gap-3 sm:gap-6">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <AppLogo size={28} />
            <span className="font-bold text-base sm:text-lg tracking-tight text-foreground">
              Award<span className="text-primary">Flix</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={`nav-${link.label}`}
                href={link.href}
                className={`px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  currentPath === link.href
                    ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Search icon */}
            <Link
              href="/search"
              className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
              aria-label="Search"
            >
              <Icon name="MagnifyingGlassIcon" size={18} />
            </Link>

            {!loading && user ? (
              <>
                {/* Notifications */}
                <button
                  className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150 relative"
                  aria-label="Notifications"
                >
                  <Icon name="BellIcon" size={18} />
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary"></span>
                </button>

                {/* User avatar dropdown */}
                <div className="relative hidden sm:block" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(v => !v)}
                    className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-white/5 transition-all duration-150"
                    aria-label="User menu"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">
                      {initials}
                    </div>
                    <span className="text-sm font-medium text-foreground max-w-[100px] truncate hidden lg:block">
                      {displayName}
                    </span>
                    <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 rounded-xl bg-card border border-border shadow-xl py-1 z-50">
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      {(isAdmin() || isSuperAdmin()) && (
                        <Link
                          href="/admin-dashboard"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-primary hover:bg-primary/10 transition-colors font-medium"
                        >
                          <Icon name="Squares2X2Icon" size={15} className="text-primary" />
                          Admin Dashboard
                        </Link>
                      )}
                      <Link
                        href="/account-settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-white/5 transition-colors"
                      >
                        <Icon name="Cog6ToothIcon" size={15} className="text-muted-foreground" />
                        Account Settings
                      </Link>
                      <Link
                        href="/billing"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-white/5 transition-colors"
                      >
                        <Icon name="CreditCardIcon" size={15} className="text-muted-foreground" />
                        Billing & Subscription
                      </Link>
                      <Link
                        href="/user-profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-white/5 transition-colors"
                      >
                        <Icon name="IdentificationIcon" size={15} className="text-muted-foreground" />
                        My Profile
                      </Link>
                      <div className="border-t border-border mt-1 pt-1">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-negative hover:bg-negative/10 transition-colors"
                        >
                          <Icon name="ArrowRightOnRectangleIcon" size={15} />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : !loading ? (
              <>
                <button
                  className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150 relative"
                  aria-label="Notifications"
                >
                  <Icon name="BellIcon" size={18} />
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary"></span>
                </button>

                <Link
                  href="/account-settings"
                  className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
                  aria-label="Account Settings"
                >
                  <Icon name="UserCircleIcon" size={20} />
                </Link>

                <Link
                  href="/user-profile"
                  className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
                  aria-label="My Profile"
                >
                  <Icon name="IdentificationIcon" size={20} />
                </Link>

                <Link
                  href="/sign-up-login-screen"
                  className="hidden sm:inline-flex btn-secondary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
                >
                  Sign In
                </Link>

                <Link
                  href="/sign-up-login-screen"
                  className="hidden sm:inline-flex btn-primary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
                >
                  Start Free
                </Link>
              </>
            ) : null}

            {/* Mobile: compact sign in or avatar */}
            {!loading && user ? (
              <button
                onClick={() => setMobileOpen(true)}
                className="sm:hidden w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary"
              >
                {initials}
              </button>
            ) : !loading ? (
              <Link
                href="/sign-up-login-screen"
                className="sm:hidden btn-primary text-xs py-1.5 px-3"
              >
                Join
              </Link>
            ) : null}

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="Bars3Icon" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100]">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-0 right-0 bottom-0 w-[280px] sm:w-72 glass-panel flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo size={24} />
                <span className="font-bold text-foreground">AwardFlix</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-full hover:bg-white/5"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            {/* Mobile user info */}
            {user && (
              <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            )}

            <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
              {navLinks.map((link) => (
                <Link
                  key={`mobile-nav-${link.label}`}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-medium ${
                    currentPath === link.href
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {(isAdmin() || isSuperAdmin()) && (
                <Link
                  href="/admin-dashboard"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-medium ${
                    currentPath === '/admin-dashboard' ? 'text-primary bg-primary/10' : 'text-primary/80 hover:text-primary hover:bg-primary/10'
                  }`}
                >
                  <Icon name="Squares2X2Icon" size={16} />
                  Admin Dashboard
                </Link>
              )}
              <Link
                href="/account-settings"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-medium ${
                  currentPath === '/account-settings' ?'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
              >
                <Icon name="UserCircleIcon" size={16} />
                Account Settings
              </Link>
              <Link
                href="/user-profile"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all text-sm font-medium ${
                  currentPath === '/user-profile' ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
              >
                <Icon name="IdentificationIcon" size={16} />
                My Profile
              </Link>
            </nav>

            <div className="p-4 border-t border-border flex flex-col gap-2">
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="btn-secondary w-full justify-center text-sm py-2.5 border-negative/30 text-negative hover:bg-negative/10"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={16} />
                  Sign Out
                </button>
              ) : (
                <>
                  <Link href="/sign-up-login-screen" onClick={() => setMobileOpen(false)} className="btn-secondary w-full justify-center text-sm py-2.5">
                    Sign In
                  </Link>
                  <Link href="/sign-up-login-screen" onClick={() => setMobileOpen(false)} className="btn-primary w-full justify-center text-sm py-2.5">
                    Start Free Trial
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}